import { Component } from '@theme/component';

/** Calendly posts its milestones from this origin only. */
const CALENDLY_ORIGIN = 'https://calendly.com';

/**
 * `<calendly-lead-tracking>` — reports a completed Calendly booking as a lead.
 *
 * Calendly renders inside a cross-origin iframe, so no tag can see the booking
 * happen: they only ever observe the parent document. Calendly announces the
 * milestone via `window.postMessage` instead, and this element is the missing
 * listener — it turns `calendly.event_scheduled` into a lead event.
 *
 * The listener itself cannot live in a Shopify Custom Pixel: those run in a
 * sandboxed iframe of their own and never receive Calendly's message. The Meta
 * side, however, has to — `fbq` is undefined in the storefront's top frame, so
 * the booking is published as `medicross:calendly_lead` and forwarded to Meta
 * inside the "Meta Lead Event" custom pixel (`pixels/meta-lead-event-pixel.js`).
 *
 * Wrap the `.calendly-inline-widget` div with it. Messages are matched against
 * this element's own iframe, so several tracked embeds can coexist on a page.
 *
 * @property {string} [data-lead-name] - `content_name` for the Meta Pixel.
 * @property {string} [data-lead-category] - `content_category` for the Meta
 *   Pixel, on both the slot-selected and the booking event.
 * @property {string} [data-lead-type] - `lead_type` for PostHog.
 * @property {string} [data-lead-value] - Optional monetary value; forwarded with
 *   `data-lead-currency` (default EUR) to Meta as `value` / `currency` when set.
 * @property {string} [data-lead-currency] - ISO currency for `data-lead-value`.
 *
 * @extends {Component}
 */
class CalendlyLeadTracking extends Component {
  /** Guards against Calendly re-posting the confirmation of the same booking. */
  #scheduled = false;

  /**
   * Same guard for the micro-conversion: Calendly re-posts
   * `date_and_time_selected` on every slot the visitor picks, and the funnel
   * only cares that they reached the step once.
   */
  #slotSelected = false;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('message', this.#handleMessage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('message', this.#handleMessage);
  }

  /**
   * @param {MessageEvent} event
   */
  #handleMessage = (event) => {
    if (event.origin !== CALENDLY_ORIGIN) return;

    const name = event.data?.event;
    if (typeof name !== 'string' || !name.startsWith('calendly.')) return;
    if (!this.#isOwnWidget(event.source)) return;

    if (name === 'calendly.date_and_time_selected') {
      if (this.#slotSelected) return;
      this.#slotSelected = true;
      this.#trackSlotSelected();
      return;
    }

    if (name !== 'calendly.event_scheduled' || this.#scheduled) return;
    this.#scheduled = true;
    this.#trackLead(eventId(event.data?.payload));
  };

  /**
   * Calendly's message reaches every listener on the page, so ignore the ones
   * coming from a different embed. Before the iframe exists there is nothing to
   * confuse us with, so an unmatched source is accepted.
   *
   * @param {MessageEventSource | null} source
   */
  #isOwnWidget(source) {
    const frames = this.querySelectorAll('iframe');
    if (!frames.length) return true;
    return Array.from(frames).some((frame) => frame.contentWindow === source);
  }

  /** Micro-conversion: the visitor picked a slot but has not confirmed yet. */
  #trackSlotSelected() {
    const { leadCategory, leadType } = this.dataset;

    // Routed like the booking itself — `fbq` lives in the "Meta Lead Event"
    // custom pixel, never in the storefront's top frame. `content_category`
    // carries `leadCategory` on both steps so the Meta parameter means one
    // thing across the funnel; `leadType` is the PostHog dimension.
    safely(() =>
      // @ts-ignore - Shopify.analytics is injected at runtime, not in the bundled typings.
      window.Shopify?.analytics?.publish('medicross:calendly_slot_selected', {
        content_category: leadCategory,
      })
    );

    // The booking funnel is analysed in PostHog, so the step has to land there
    // too — `calendly_date_selected` → `calendly_event_scheduled`.
    safely(() => window.phCapture?.('calendly_date_selected', { lead_type: leadType }));
  }

  /**
   * @param {string} id - Dedup key shared with a future server-side event.
   */
  #trackLead(id) {
    const { leadName, leadCategory, leadType, leadValue, leadCurrency } = this.dataset;
    const money = leadValue ? { value: Number(leadValue), currency: leadCurrency || 'EUR' } : {};

    // Meta is reached through the "Meta Lead Event" custom pixel, not from here:
    // `fbq` only exists inside that pixel's sandbox, and Shopify enforces the
    // MARKETING consent purpose on it for us. `event_id` becomes Meta's eventID
    // there, so the Conversions API still deduplicates this booking against the
    // server-side twin built from the same `invitee.created` URI.
    safely(() =>
      // @ts-ignore - Shopify.analytics is injected at runtime, not in the bundled typings.
      window.Shopify?.analytics?.publish('medicross:calendly_lead', {
        event_id: id,
        content_name: leadName,
        content_category: leadCategory,
        lead_type: leadType,
        ...money,
      })
    );

    safely(() => window.phCapture?.('calendly_event_scheduled', { lead_type: leadType, calendly_event_id: id }));
  }
}

/**
 * Sends one tag call in isolation — the pixels are third-party, and one of them
 * throwing must not stop the remaining events from being reported.
 *
 * @param {() => void} send
 */
function safely(send) {
  try {
    send();
  } catch (_) {
    // no-op
  }
}

/**
 * Derives the dedup key from the invitee URI Calendly sends with the booking —
 * the same URI arrives in the `invitee.created` webhook, so a server-side
 * Conversions API event can reuse it verbatim and Meta will collapse the pair.
 * Falls back to a random id when the payload is missing.
 *
 * @param {{ invitee?: { uri?: string } }} [payload]
 * @returns {string}
 */
function eventId(payload) {
  const uri = payload?.invitee?.uri;
  const uuid = typeof uri === 'string' ? uri.split('/').pop() : null;
  return `cal-${uuid || Math.random().toString(36).slice(2, 12)}`;
}

if (!customElements.get('calendly-lead-tracking')) {
  customElements.define('calendly-lead-tracking', CalendlyLeadTracking);
}
